// src/data/emgClinicalCriteria.ts

import { muscleDatabase } from './muscleData';
import { findNerveEntry } from './nerveData';
import type { NCSTestResult } from '../types/ncs';

/** Estándares térmicos cutáneos por territorio (AANEM / práctica clínica). */
export const TEMPERATURE_STANDARDS = {
  UPPER_LIMB: 32,
  LOWER_LIMB: 30,
  /** Factor multiplicativo por °C bajo el estándar: amplitud observada se corrige hacia abajo (el frío la infla). */
  AMPLITUDE_CORRECTION_FACTOR: 0.025,
} as const;

/** Duración máxima distal del CMAP (ms) por nervio — referencia clínica para dispersión temporal (EAN/PNS). */
export const CMAP_MAX_DURATION_THRESHOLDS: Record<string, number> = {
  mediano: 6.6,
  cubital: 6.7,
  peroneo: 7.6,
  tibial: 8.8,
};

/**
 * Umbrales compartidos STC (Síndrome del Túnel Carpiano) — fuente única Sprint 4.
 * Usado por: ctsAnalyzer, DiagnosticPatternAnalyzer, diagnosticPatterns (referencia).
 */
export const CARPAL_TUNNEL_CRITERIA = {
  sensory: {
    peakLatencyMs: 3.5,
    latencyDifferenceMs: 0.4,
    palmWristLatencyMs: 2.2,
    conductionVelocityMinMs: 49,
  },
  motor: {
    distalLatencyAbnormalMs: 4.2,
    lumbricalInterosseiDiffMs: 0.5,
    moderateLatencyMs: 4.5,
    severeLatencyMs: 6.5,
    cmapReducedMv: 4.0,
    cmapVerySevereMv: 0.2,
  },
  axonal: {
    snapSeverelyReducedUv: 2.0,
  },
  pattern: {
    /** Latencia mediana sospechosa en motor de patrón (DiagnosticPatternAnalyzer) */
    medianLatencySuspiciousMs: 4.0,
    /** STC leve Bland grado 2: LMD aún ≤ este valor */
    mildMotorLatencyMaxMs: 4.5,
  },
} as const;

export function isMedianNerveName(nerve: string | undefined): boolean {
  if (!nerve) return false;
  const n = nerve
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return n.includes('median') || n.includes('mediano');
}

export function isCtsMotorDistalLatencyAbnormal(latencyMs: number): boolean {
  return latencyMs > CARPAL_TUNNEL_CRITERIA.motor.distalLatencyAbnormalMs;
}

export function isCtsSensoryPeakLatencyAbnormal(latencyMs: number): boolean {
  return latencyMs > CARPAL_TUNNEL_CRITERIA.sensory.peakLatencyMs;
}

export function isCtsPatternMedianLatencySuspicious(latencyMs: number): boolean {
  return latencyMs > CARPAL_TUNNEL_CRITERIA.pattern.medianLatencySuspiciousMs;
}

/** STC leve: sensitivo anormal, motor distal ≤ 4.5 ms, amplitud motor preservada */
export function isMildCtsElectrophysiology(params: {
  sensoryPeakLatencyMs: number;
  motorDistalLatencyMs: number;
  motorAmplitudeMv: number;
}): boolean {
  const c = CARPAL_TUNNEL_CRITERIA;
  return (
    params.sensoryPeakLatencyMs > c.sensory.peakLatencyMs &&
    params.motorDistalLatencyMs <= c.pattern.mildMotorLatencyMaxMs &&
    params.motorAmplitudeMv >= c.motor.cmapReducedMv
  );
}

const LOWER_LIMB_NERVE_RE = /(tibial|peroneal|sural|fibular|pedis|plantar|saphenous)/i;

export function isLowerLimbNerveFromName(nerveOrMuscleLabel: string): boolean {
  return LOWER_LIMB_NERVE_RE.test(nerveOrMuscleLabel);
}

export function getNerveReferenceValues(resultOrNerve: NCSTestResult | string): NerveReferenceValues | null {
  const nerveName = typeof resultOrNerve === 'string' ? resultOrNerve : resultOrNerve.nerve;
  const resultReference = typeof resultOrNerve === 'string' ? undefined : resultOrNerve.referenceValues;
  const studyType = typeof resultOrNerve === 'string' ? undefined : resultOrNerve.type;

  if (
    resultReference?.latency?.max !== undefined &&
    resultReference.amplitude?.min !== undefined &&
    resultReference.velocity?.min !== undefined
  ) {
    return {
      latencyULN: resultReference.latency.max,
      amplitudeLLN: resultReference.amplitude.min,
      velocityLLN: resultReference.velocity.min,
    };
  }

  const entry = findNerveEntry(nerveName, studyType);
  const reference = entry?.referenceValues;

  if (!reference) return null;

  return {
    latencyULN: reference.latency.max,
    amplitudeLLN: reference.amplitude.min,
    velocityLLN: reference.velocity.min,
  };
}

export function evaluateDemyelinationCriteria(
  actualAmplitude: number,
  actualVelocity: number | undefined,
  actualLatency: number | undefined,
  reference: NerveReferenceValues
): DemyelinationEvaluation {
  const isUninterpretable = actualAmplitude < 0.5;
  const hasAmplitudeDependentAxonalLoss = actualAmplitude < reference.amplitudeLLN * 0.8;
  const velocityMultiplier = hasAmplitudeDependentAxonalLoss ? 0.6 : 0.7;
  const latencyMultiplier = hasAmplitudeDependentAxonalLoss ? 1.5 : 1.3;
  const dynamicVelocityThreshold = reference.velocityLLN * velocityMultiplier;
  const dynamicLatencyThreshold = reference.latencyULN * latencyMultiplier;

  return {
    isUninterpretable,
    hasAmplitudeDependentAxonalLoss,
    isVelocityDemyelinating:
      !isUninterpretable &&
      actualVelocity !== undefined &&
      Number.isFinite(actualVelocity) &&
      actualVelocity < dynamicVelocityThreshold,
    isLatencyDemyelinating:
      !isUninterpretable &&
      actualLatency !== undefined &&
      Number.isFinite(actualLatency) &&
      actualLatency > dynamicLatencyThreshold,
    thresholdsUsed: {
      velocity: dynamicVelocityThreshold,
      latency: dynamicLatencyThreshold,
    },
    reference,
  };
}

export type GoldCoastRegion = 'bulbar' | 'cervical' | 'thoracic' | 'lumbosacral';

export interface LMNMuscleEvaluationInput {
  muscleName?: string;
  muscleOrNerveName?: string;
  spontaneousActivity?: {
    fibrillations?: boolean | number;
    positiveWaves?: boolean | number;
    positiveSharpWaves?: boolean | number;
    fasciculations?: boolean | { present: boolean };
  };
  spontaneousActivitySummary?: string;
  motorUnitPotentials?: {
    amplitude?: number;
    duration?: number;
    polyphasia?: number;
    phases?: number;
    amplitudeLabel?: string;
    durationLabel?: string;
    polyphasiaLabel?: string;
    configuration?: string;
  };
  mupAmplitude?: number;
  mupDuration?: number;
  mupPolyphasia?: number;
  status?: 'normal' | 'abnormal' | string;
}

export interface LMNSignEvaluation {
  muscleName: string;
  region: GoldCoastRegion | null;
  hasLMNSigns: boolean;
  activeDenervation: boolean;
  awajiFasciculationsWithChronicMuap: boolean;
  isolatedFasciculations: boolean;
  chronicMuapSigns: {
    increasedAmplitude: boolean;
    prolongedDuration: boolean;
    increasedPolyphasia: boolean;
  };
  rationale: string;
}

export interface GoldCoastLMNRegionSummary {
  affectedRegions: GoldCoastRegion[];
  affectedMuscles: LMNSignEvaluation[];
  isolatedFasciculationMuscles: LMNSignEvaluation[];
}

export type RootContributionRole = 'primary' | 'secondary';

export interface RootContribution {
  root: string;
  weight: number;
  role: RootContributionRole;
}

export interface MyotomeMuscleMapping {
  muscleName: string;
  roots: string[];
  primaryRoots: string[];
  /** Contribución ponderada por raíz (suma ≈ 1). Si falta, se deriva de roots/primaryRoots. */
  rootContributions?: RootContribution[];
  /** Segmento espinal para paraespinales (p. ej. L4). */
  segment?: string;
  peripheralNerve: string;
  isParaspinal: boolean;
}

export const RADICULAR_EVIDENCE_WEIGHTS = {
  activeDenervation: 1.0,
  chronicNeurogenic: 0.6,
  reducedRecruitment: 0.35,
} as const;

export const RADICULAR_ROOT_THRESHOLDS = {
  confirmed: 1.15,
  probable: 0.72,
  possible: 0.4,
} as const;

export interface NerveReferenceValues {
  latencyULN: number;
  amplitudeLLN: number;
  velocityLLN: number;
}

export interface DemyelinationEvaluation {
  isVelocityDemyelinating: boolean;
  isLatencyDemyelinating: boolean;
  isUninterpretable: boolean;
  hasAmplitudeDependentAxonalLoss: boolean;
  thresholdsUsed: {
    velocity: number;
    latency: number;
  };
  reference: NerveReferenceValues;
}

const CHRONIC_NEUROGENIC_MUAP_THRESHOLDS = {
  amplitudeUv: 2000,
  durationMs: 15,
  polyphasiaPercent: 20,
  phases: 4,
} as const;

export const ROOT_ORDER = ['C5', 'C6', 'C7', 'C8', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12', 'L2', 'L3', 'L4', 'L5', 'S1', 'S2'] as const;

const MYOTOME_MUSCLE_MAP: Array<MyotomeMuscleMapping & { aliases: string[] }> = [
  { muscleName: 'Deltoides', aliases: ['deltoid', 'deltoides'], roots: ['C5', 'C6'], primaryRoots: ['C5'], peripheralNerve: 'axillary', isParaspinal: false },
  { muscleName: 'Bíceps braquial', aliases: ['biceps', 'biceps brachii', 'biceps braquial'], roots: ['C5', 'C6'], primaryRoots: ['C6'], peripheralNerve: 'musculocutaneous', isParaspinal: false },
  { muscleName: 'Braquiorradial', aliases: ['brachioradialis', 'braquiorradial'], roots: ['C5', 'C6'], primaryRoots: ['C6'], peripheralNerve: 'radial', isParaspinal: false },
  { muscleName: 'Pronador redondo', aliases: ['pronator teres', 'pronador redondo'], roots: ['C6', 'C7'], primaryRoots: ['C6'], peripheralNerve: 'median', isParaspinal: false },
  { muscleName: 'Tríceps braquial', aliases: ['triceps', 'triceps brachii', 'triceps braquial'], roots: ['C7', 'C8'], primaryRoots: ['C7'], peripheralNerve: 'radial', isParaspinal: false },
  { muscleName: 'Extensor común de los dedos', aliases: ['extensor digitorum', 'extensor comun', 'extensor común'], roots: ['C7', 'C8'], primaryRoots: ['C7'], peripheralNerve: 'posterior_interosseous', isParaspinal: false },
  { muscleName: 'Primer interóseo dorsal', aliases: ['first dorsal interosseous', 'primer interoseo dorsal', 'primer interóseo dorsal', 'fdi'], roots: ['C8', 'T1'], primaryRoots: ['C8'], peripheralNerve: 'ulnar', isParaspinal: false },
  { muscleName: 'Abductor corto del pulgar', aliases: ['abductor pollicis brevis', 'abductor corto del pulgar', 'apb'], roots: ['C8', 'T1'], primaryRoots: ['T1'], peripheralNerve: 'median', isParaspinal: false },
  { muscleName: 'Paraespinales cervicales', aliases: ['cervical paraspinal', 'cervical paraspinals', 'paraspinals c5', 'paraspinals c6', 'paraspinals c7', 'paraspinals c8', 'paraespinal cervical'], roots: ['C5', 'C6', 'C7', 'C8'], primaryRoots: ['C6', 'C7'], peripheralNerve: 'posterior_ramus', isParaspinal: true },
  { muscleName: 'Paraespinales torácicos', aliases: ['thoracic paraspinal', 'thoracic paraspinals', 'paraspinals thoracic', 'paraespinal toracico', 'paraespinal torácico'], roots: ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'], primaryRoots: ['T6'], peripheralNerve: 'posterior_ramus', isParaspinal: true },
  { muscleName: 'Iliopsoas', aliases: ['iliopsoas', 'psoas'], roots: ['L2', 'L3'], primaryRoots: ['L2', 'L3'], peripheralNerve: 'femoral', isParaspinal: false },
  { muscleName: 'Vasto medial', aliases: ['vastus medialis', 'vasto medial', 'quadriceps', 'cuadriceps'], roots: ['L2', 'L3', 'L4'], primaryRoots: ['L3', 'L4'], rootContributions: [{ root: 'L4', weight: 0.55, role: 'primary' }, { root: 'L3', weight: 0.3, role: 'secondary' }, { root: 'L2', weight: 0.15, role: 'secondary' }], peripheralNerve: 'femoral', isParaspinal: false },
  { muscleName: 'Vasto lateral', aliases: ['vastus lateralis', 'vasto lateral', 'vasto lateralis'], roots: ['L2', 'L3', 'L4'], primaryRoots: ['L3', 'L4'], rootContributions: [{ root: 'L4', weight: 0.55, role: 'primary' }, { root: 'L3', weight: 0.3, role: 'secondary' }, { root: 'L2', weight: 0.15, role: 'secondary' }], peripheralNerve: 'femoral', isParaspinal: false },
  { muscleName: 'Tibial anterior', aliases: ['tibialis anterior', 'tibial anterior'], roots: ['L4', 'L5'], primaryRoots: ['L5'], rootContributions: [{ root: 'L5', weight: 0.65, role: 'primary' }, { root: 'L4', weight: 0.35, role: 'secondary' }], peripheralNerve: 'deep_peroneal', isParaspinal: false },
  { muscleName: 'Peroneo largo', aliases: ['peroneus longus', 'fibularis longus', 'peroneo largo'], roots: ['L5', 'S1'], primaryRoots: ['L5'], rootContributions: [{ root: 'L5', weight: 0.7, role: 'primary' }, { root: 'S1', weight: 0.3, role: 'secondary' }], peripheralNerve: 'superficial_peroneal', isParaspinal: false },
  { muscleName: 'Glúteo medio', aliases: ['gluteus medius', 'gluteo medio', 'glúteo medio'], roots: ['L4', 'L5', 'S1'], primaryRoots: ['L5'], rootContributions: [{ root: 'L5', weight: 0.5, role: 'primary' }, { root: 'S1', weight: 0.3, role: 'secondary' }, { root: 'L4', weight: 0.2, role: 'secondary' }], peripheralNerve: 'superior_gluteal', isParaspinal: false },
  { muscleName: 'Tensor de la fascia lata', aliases: ['tensor fascia lata', 'tensor de la fascia lata', 'tfl'], roots: ['L4', 'L5', 'S1'], primaryRoots: ['L5'], rootContributions: [{ root: 'L5', weight: 0.5, role: 'primary' }, { root: 'S1', weight: 0.3, role: 'secondary' }, { root: 'L4', weight: 0.2, role: 'secondary' }], peripheralNerve: 'superior_gluteal', isParaspinal: false },
  { muscleName: 'Bíceps femoral (cabeza corta)', aliases: ['biceps femoris short head', 'biceps femoris (short head)', 'biceps femoris cabeza corta', 'biceps femoral cabeza corta'], roots: ['L5', 'S1'], primaryRoots: ['L5', 'S1'], rootContributions: [{ root: 'L5', weight: 0.55, role: 'primary' }, { root: 'S1', weight: 0.45, role: 'secondary' }], peripheralNerve: 'common_peroneal', isParaspinal: false },
  { muscleName: 'Bíceps femoral (cabeza larga)', aliases: ['biceps femoris long head', 'biceps femoris (long head)', 'biceps femoral cabeza larga'], roots: ['L5', 'S1'], primaryRoots: ['S1'], rootContributions: [{ root: 'S1', weight: 0.8, role: 'primary' }, { root: 'L5', weight: 0.2, role: 'secondary' }], peripheralNerve: 'sciatic', isParaspinal: false },
  { muscleName: 'Gastrocnemio', aliases: ['gastrocnemius', 'gastrocnemio'], roots: ['S1', 'S2'], primaryRoots: ['S1'], rootContributions: [{ root: 'S1', weight: 0.85, role: 'primary' }, { root: 'S2', weight: 0.15, role: 'secondary' }], peripheralNerve: 'tibial', isParaspinal: false },
  { muscleName: 'Sóleo', aliases: ['soleus', 'soleo', 'sóleo'], roots: ['S1', 'S2'], primaryRoots: ['S1'], rootContributions: [{ root: 'S1', weight: 0.85, role: 'primary' }, { root: 'S2', weight: 0.15, role: 'secondary' }], peripheralNerve: 'tibial', isParaspinal: false },
  { muscleName: 'Abductor hallucis', aliases: ['abductor hallucis', 'abductor del hallux'], roots: ['S1', 'S2'], primaryRoots: ['S1'], rootContributions: [{ root: 'S1', weight: 0.85, role: 'primary' }, { root: 'S2', weight: 0.15, role: 'secondary' }], peripheralNerve: 'tibial', isParaspinal: false },
  { muscleName: 'Paraespinal L3', aliases: ['l3 paraspinal', 'l3 paraespinal', 'paraspinal l3', 'paraspinals l3'], roots: ['L3'], primaryRoots: ['L3'], rootContributions: [{ root: 'L3', weight: 1, role: 'primary' }], segment: 'L3', peripheralNerve: 'posterior_ramus', isParaspinal: true },
  { muscleName: 'Paraespinal L4', aliases: ['l4 paraspinal', 'l4 paraespinal', 'paraspinal l4', 'paraspinals l4'], roots: ['L4'], primaryRoots: ['L4'], rootContributions: [{ root: 'L4', weight: 1, role: 'primary' }], segment: 'L4', peripheralNerve: 'posterior_ramus', isParaspinal: true },
  { muscleName: 'Paraespinal L5', aliases: ['l5 paraspinal', 'l5 paraespinal', 'paraspinal l5', 'paraspinals l5'], roots: ['L5'], primaryRoots: ['L5'], rootContributions: [{ root: 'L5', weight: 1, role: 'primary' }], segment: 'L5', peripheralNerve: 'posterior_ramus', isParaspinal: true },
  { muscleName: 'Paraespinal S1', aliases: ['s1 paraspinal', 's1 paraespinal', 'paraspinal s1', 'paraspinals s1'], roots: ['S1'], primaryRoots: ['S1'], rootContributions: [{ root: 'S1', weight: 1, role: 'primary' }], segment: 'S1', peripheralNerve: 'posterior_ramus', isParaspinal: true },
  { muscleName: 'Paraespinales lumbares', aliases: ['lumbar paraspinal', 'lumbar paraspinals', 'lumbosacral paraspinal', 'paraespinal lumbar'], roots: ['L4', 'L5', 'S1'], primaryRoots: ['L5', 'S1'], rootContributions: [{ root: 'L5', weight: 0.4, role: 'primary' }, { root: 'S1', weight: 0.35, role: 'primary' }, { root: 'L4', weight: 0.25, role: 'secondary' }], peripheralNerve: 'posterior_ramus', isParaspinal: true },
];

function normalizeLabel(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]/g, ' ')
    .trim();
}

function expandRootRange(rootText: string): string[] {
  const normalized = rootText.toUpperCase().replace(/\s/g, '');
  const explicitRoots = normalized.match(/[CTLS]\d{1,2}/g);
  if (!explicitRoots?.length) return [];

  if (!normalized.includes('-') || explicitRoots.length < 2) {
    return explicitRoots;
  }

  const startIndex = ROOT_ORDER.indexOf(explicitRoots[0] as typeof ROOT_ORDER[number]);
  const endIndex = ROOT_ORDER.indexOf(explicitRoots[explicitRoots.length - 1] as typeof ROOT_ORDER[number]);
  if (startIndex < 0 || endIndex < 0 || startIndex > endIndex) {
    return explicitRoots;
  }

  return [...ROOT_ORDER.slice(startIndex, endIndex + 1)];
}

function hasPositiveSpontaneousValue(value: boolean | number | { present: boolean } | undefined): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  return value?.present === true;
}

function summaryIncludesAny(summary: string | undefined, patterns: RegExp[]): boolean {
  if (!summary) return false;
  return patterns.some(pattern => pattern.test(summary));
}

export function getGoldCoastRegionForMuscle(muscleLabel: string): GoldCoastRegion | null {
  const normalized = normalizeLabel(muscleLabel);

  const muscleDefinition = muscleDatabase.find(muscle => {
    const id = normalizeLabel(muscle.id);
    const name = normalizeLabel(muscle.name);
    return id === normalized || name === normalized || normalized.includes(id) || normalized.includes(name);
  });

  const root = muscleDefinition?.innervation.root.toUpperCase() || '';
  if (/CN|BULBAR/.test(root)) return 'bulbar';
  if (/C\d/.test(root)) return 'cervical';
  if (/T\d/.test(root)) return 'thoracic';
  if (/[LS]\d/.test(root)) return 'lumbosacral';

  if (/(lengua|tongue|geniogloss|facial|frontal|orbicular|masseter|bulbar|cranial)/i.test(normalized)) {
    return 'bulbar';
  }
  if (/(toracic|thoracic|paraspinal tor|paraespinal tor|intercost|abdomen|abdominal|rectus abdominis)/i.test(normalized)) {
    return 'thoracic';
  }
  if (/(lumbar|lumbosacral|sacro|sacral)/i.test(normalized)) {
    return 'lumbosacral';
  }
  if (/(cervical|biceps|triceps|deltoid|interoseo|interosseous|abductor pollicis|thenar|hypothenar|ulnar|cubital|median|mediano|radial)/i.test(normalized)) {
    return 'cervical';
  }
  if (/(tibial|gastrocnem|soleus|vastus|quadriceps|peroneal|fibular|pedis|hallucis|iliopsoas|glute)/i.test(normalized)) {
    return 'lumbosacral';
  }

  return null;
}

function resolveSegmentedParaspinalMapping(normalized: string): MyotomeMuscleMapping | null {
  const patterns = [
    /\b(l[1-5]|s[12]|c[5678]|t\d{1,2})\s+(paraespinal|paraspinal|paravertebral)\b/,
    /\b(paraespinal|paraspinal|paravertebral)\s+(l[1-5]|s[12]|c[5678]|t\d{1,2})\b/,
    /\bparaspinals?\s+(l[1-5]|s[12]|c[5678]|t\d{1,2})\b/,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (!match) continue;
    const rootToken = (match[1] || match[2] || '').toUpperCase();
    if (!/^[CLST]\d{1,2}$/.test(rootToken)) continue;
    return {
      muscleName: `Paraespinal ${rootToken}`,
      roots: [rootToken],
      primaryRoots: [rootToken],
      rootContributions: [{ root: rootToken, weight: 1, role: 'primary' }],
      segment: rootToken,
      peripheralNerve: 'posterior_ramus',
      isParaspinal: true,
    };
  }

  return null;
}

/** Deriva contribuciones ponderadas cuando el mapa solo tiene roots/primaryRoots legacy. */
export function getRootContributionsForMapping(mapping: MyotomeMuscleMapping): RootContribution[] {
  if (mapping.rootContributions?.length) {
    return mapping.rootContributions;
  }

  const primarySet = new Set(mapping.primaryRoots.map(root => root.toUpperCase()));
  const secondaryRoots = mapping.roots
    .map(root => root.toUpperCase())
    .filter(root => !primarySet.has(root));

  const primaryWeight = primarySet.size > 0 ? 0.7 / primarySet.size : 0;
  const secondaryWeight = secondaryRoots.length > 0 ? 0.3 / secondaryRoots.length : 0;

  const contributions: RootContribution[] = [
    ...mapping.primaryRoots.map(root => ({
      root: root.toUpperCase(),
      weight: primaryWeight,
      role: 'primary' as const,
    })),
    ...secondaryRoots.map(root => ({
      root,
      weight: secondaryWeight,
      role: 'secondary' as const,
    })),
  ];

  const total = contributions.reduce((sum, item) => sum + item.weight, 0);
  if (total <= 0) return contributions;
  return contributions.map(item => ({ ...item, weight: item.weight / total }));
}

export function computeMuscleRadicularEvidence(
  lmn: LMNSignEvaluation,
  recruitmentPattern?: string
): number {
  if (lmn.activeDenervation) return RADICULAR_EVIDENCE_WEIGHTS.activeDenervation;
  if (Object.values(lmn.chronicMuapSigns).some(Boolean)) {
    return RADICULAR_EVIDENCE_WEIGHTS.chronicNeurogenic;
  }
  const recruitment = normalizeLabel(recruitmentPattern || '');
  if (/reduced|decreased|diminished|incomplete|submax|parcial/.test(recruitment)) {
    return RADICULAR_EVIDENCE_WEIGHTS.reducedRecruitment;
  }
  return 0;
}

export function getMyotomeMappingForMuscle(muscleLabel: string): MyotomeMuscleMapping | null {
  const normalized = normalizeLabel(muscleLabel);

  const segmentedParaspinal = resolveSegmentedParaspinalMapping(normalized);
  if (segmentedParaspinal) return segmentedParaspinal;

  const manualMapping = MYOTOME_MUSCLE_MAP.find(mapping =>
    mapping.aliases.some(alias => {
      const normalizedAlias = normalizeLabel(alias);
      if (normalized === normalizedAlias) return true;
      // Word-boundary partial match: alias must appear as a complete token (not as a substring of another word)
      const escaped = normalizedAlias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(?:^|\\s)${escaped}(?:\\s|$)`).test(normalized);
    })
  );

  if (manualMapping) {
    const { aliases, ...mapping } = manualMapping;
    return mapping;
  }

  const muscleDefinition = muscleDatabase.find(muscle => {
    const id = normalizeLabel(muscle.id);
    const name = normalizeLabel(muscle.name);
    return id === normalized || name === normalized || normalized.includes(id) || normalized.includes(name);
  });

  if (!muscleDefinition) return null;

  const roots = expandRootRange(muscleDefinition.innervation.root);
  const primaryRoots = roots.length > 0 ? [roots[Math.floor((roots.length - 1) / 2)]] : [];
  const isParaspinal = /(paraspinal|paraespinal|paravertebral|multifidus)/i.test(normalized);

  return {
    muscleName: muscleDefinition.name,
    roots,
    primaryRoots,
    peripheralNerve: muscleDefinition.innervation.nerve,
    isParaspinal,
  };
}

export function evaluateLMNSigns(muscle: LMNMuscleEvaluationInput): LMNSignEvaluation {
  const muscleName = muscle.muscleName || muscle.muscleOrNerveName || 'Músculo no especificado';
  const summary = normalizeLabel(muscle.spontaneousActivitySummary || '');
  const spontaneous = muscle.spontaneousActivity;
  const mup = muscle.motorUnitPotentials;

  const hasFibrillations =
    hasPositiveSpontaneousValue(spontaneous?.fibrillations) ||
    summaryIncludesAny(summary, [/fibril/]);
  const hasPositiveWaves =
    hasPositiveSpontaneousValue(spontaneous?.positiveWaves) ||
    hasPositiveSpontaneousValue(spontaneous?.positiveSharpWaves) ||
    summaryIncludesAny(summary, [/onda.*positiv/, /positive.*wave/, /positive.*sharp/]);
  const hasFasciculations =
    hasPositiveSpontaneousValue(spontaneous?.fasciculations) ||
    summaryIncludesAny(summary, [/fascicul/]);

  const amplitude = mup?.amplitude ?? muscle.mupAmplitude;
  const duration = mup?.duration ?? muscle.mupDuration;
  const polyphasia = mup?.polyphasia ?? muscle.mupPolyphasia;
  const phases = mup?.phases;
  const amplitudeLabel = normalizeLabel(mup?.amplitudeLabel || '');
  const durationLabel = normalizeLabel(mup?.durationLabel || '');
  const polyphasiaLabel = normalizeLabel(`${mup?.polyphasiaLabel || ''} ${mup?.configuration || ''}`);

  const chronicMuapSigns = {
    increasedAmplitude:
      (typeof amplitude === 'number' &&
        Number.isFinite(amplitude) &&
        amplitude > CHRONIC_NEUROGENIC_MUAP_THRESHOLDS.amplitudeUv) ||
      summaryIncludesAny(amplitudeLabel, [/increased/, /aumentad/, /large/, /giant/]),
    prolongedDuration:
      (typeof duration === 'number' &&
        Number.isFinite(duration) &&
        duration > CHRONIC_NEUROGENIC_MUAP_THRESHOLDS.durationMs) ||
      summaryIncludesAny(durationLabel, [/increased/, /aumentad/, /long/, /prolong/]),
    increasedPolyphasia:
      (typeof polyphasia === 'number' &&
        Number.isFinite(polyphasia) &&
        polyphasia > CHRONIC_NEUROGENIC_MUAP_THRESHOLDS.polyphasiaPercent) ||
      (typeof phases === 'number' &&
        Number.isFinite(phases) &&
        phases > CHRONIC_NEUROGENIC_MUAP_THRESHOLDS.phases) ||
      summaryIncludesAny(polyphasiaLabel, [/many/, /abundant/, /marked/, /poly/, /polifas/]),
  };

  const activeDenervation = hasFibrillations || hasPositiveWaves;
  const hasChronicNeurogenicMuap = Object.values(chronicMuapSigns).some(Boolean);
  const awajiFasciculationsWithChronicMuap = hasFasciculations && hasChronicNeurogenicMuap;
  const isolatedFasciculations = hasFasciculations && !activeDenervation && !hasChronicNeurogenicMuap;
  const hasLMNSigns = activeDenervation || awajiFasciculationsWithChronicMuap;

  return {
    muscleName,
    region: getGoldCoastRegionForMuscle(muscleName),
    hasLMNSigns,
    activeDenervation,
    awajiFasciculationsWithChronicMuap,
    isolatedFasciculations,
    chronicMuapSigns,
    rationale: activeDenervation
      ? 'Denervación activa clásica: fibrilaciones y/u ondas agudas positivas.'
      : awajiFasciculationsWithChronicMuap
        ? 'Criterio Awaji: fasciculaciones con PAUMs neurogénicos crónicos.'
        : isolatedFasciculations
          ? 'Fasciculaciones aisladas sin cambios crónicos de PAUM; no puntúa para ELA.'
          : 'Sin signos NMI válidos para ELA.',
  };
}

export function summarizeGoldCoastLMNRegions(muscles: LMNMuscleEvaluationInput[]): GoldCoastLMNRegionSummary {
  const evaluations = muscles.map(evaluateLMNSigns);
  const affectedMuscles = evaluations.filter(item => item.hasLMNSigns && item.region !== null);
  const affectedRegions = Array.from(
    new Set(affectedMuscles.map(item => item.region).filter((region): region is GoldCoastRegion => region !== null))
  );

  return {
    affectedRegions,
    affectedMuscles,
    isolatedFasciculationMuscles: evaluations.filter(item => item.isolatedFasciculations),
  };
}

/** Temperatura de referencia (°C) según nervio o etiqueta de músculo/nervio. */
export function ncsStandardTemperatureC(nerveName: string): number {
  return isLowerLimbNerveFromName(nerveName) ? TEMPERATURE_STANDARDS.LOWER_LIMB : TEMPERATURE_STANDARDS.UPPER_LIMB;
}

export function correctedNcsAmplitudeForCold(
  amplitudeMv: number,
  temperatureC: number | undefined,
  nerveName: string
): { corrected: number; applied: boolean; standard: number } {
  const standard = ncsStandardTemperatureC(nerveName);
  if (temperatureC === undefined || temperatureC === null || !Number.isFinite(temperatureC) || temperatureC >= standard) {
    return { corrected: amplitudeMv, applied: false, standard };
  }
  const diff = standard - temperatureC;
  const factor = Math.max(0.5, 1 - TEMPERATURE_STANDARDS.AMPLITUDE_CORRECTION_FACTOR * diff);
  return {
    corrected: Math.max(0, amplitudeMv * factor),
    applied: true,
    standard,
  };
}

/** Hay al menos un registro NCS con temperatura registrada por debajo del estándar del territorio. */
export function hasNcsLowTemperatureRecords(results: NCSTestResult[]): boolean {
  return results.some(r => {
    if (r.temperature === undefined || r.temperature === null || !Number.isFinite(r.temperature)) {
      return false;
    }
    return r.temperature < ncsStandardTemperatureC(r.nerve);
  });
}

export interface AgeAdjustedThresholds {
  young: { min: number; max: number }; // 18-39 años
  middle: { min: number; max: number }; // 40-59 años
  older: { min: number; max: number }; // 60+ años
}

export interface EMGThresholds {
  duration: {
    normal: number;
    severe: number;
    ageAdjusted?: AgeAdjustedThresholds;
  };
  amplitude: {
    normal: number;
    severe: number;
    ageAdjusted?: AgeAdjustedThresholds;
  };
  polyphasia: {
    normal: number;
    severe: number;
  };
}

export interface CorrectionFactors {
  temperature: {
    duration: number;    // ms/°C
    amplitude: number;   // mV/°C
    velocity: number;    // m/s/°C
  };
  height: {
    duration: number;    // ms/cm
    velocity: number;    // m/s/cm
  };
  age: {
    velocityPerYear: number;     // % reduction per year after 60
    amplitudePerYear: number;    // % reduction per year after 60
    durationPerYear: number;     // ms increase per year after 60
  };
}

export interface NCSThresholds {
  axonal: {
    cMAPAmplitude: number;
    sNAPAmplitude: number;
    conductionVelocity: number;
  };
  demyelinating: {
    velocityReduction: number;
    latencyProlongation: number;
    conductionBlock: number; // % amplitude drop
    temporalDispersion: number;
  };
}

export interface CriterionDefinition {
  id: string;
  description: string;
  weight: number;
  specificity: number;
  evaluationFunction: string; // Nombre de la función que evalúa este criterio
  severityLevels?: {
    mild: any;
    moderate: any;
    severe: any;
  };
}

export interface PatternCriteria {
  neuropathic: CriterionDefinition[];
  axonal: CriterionDefinition[];
  demyelinating: CriterionDefinition[];
  myopathic: CriterionDefinition[];
  motor_neuron_disease: CriterionDefinition[]; // 🔥 NUEVO - PRIORIDAD 4
}

// Umbrales clínicos actualizados con correcciones por edad
export const CLINICAL_THRESHOLDS = {
  // Umbrales neuropáticos con ajustes por edad
  NEUROPATHIC: {
    duration: {
      normal: 15,
      severe: 20,
      ageAdjusted: {
        young: { min: 8, max: 15 },
        middle: { min: 8, max: 17 },
        older: { min: 8, max: 20 }
      }
    },
    amplitude: {
      normal: 5,
      severe: 7,
      ageAdjusted: {
        young: { min: 3, max: 20 },
        middle: { min: 2.5, max: 18 },
        older: { min: 2, max: 15 }
      }
    },
    polyphasia: {
      normal: 25,
      severe: 35
    }
  } as EMGThresholds,

  // Umbrales miopáticos
  MYOPATHIC: {
    duration: {
      normal: 8,
      severe: 5
    },
    amplitude: {
      normal: 2,
      severe: 1
    },
    polyphasia: {
      normal: 30,
      severe: 40
    }
  } as EMGThresholds,

  // Umbrales para NCS
  NCS: {
    axonal: {
      cMAPAmplitude: 80, // % del límite inferior normal
      sNAPAmplitude: 50, // % del límite inferior normal
      conductionVelocity: 80 // % del límite inferior normal
    },
    demyelinating: {
      velocityReduction: 70, // % del límite inferior normal
      latencyProlongation: 130, // % del límite superior normal
      conductionBlock: 30, // % de caída de amplitud (EAN/PNS 2021)
      temporalDispersion: 30 // % aumento de duración proximal vs distal
    }
  } as NCSThresholds
};

// 🔥 FACTORES DE CORRECCIÓN CORREGIDOS - PRIORIDAD 2
export const CORRECTION_FACTORS: CorrectionFactors = {
  temperature: {
    duration: 0.13,    // ms/°C - AUMENTA con frío (correcto)
    amplitude: 0.04,   // %/°C - DISMINUYE con frío (corregido de -0.04 a +0.04)
    velocity: 1.5      // m/s/°C - DISMINUYE con frío (correcto)
  },
  height: {
    duration: 0.01,    // ms/cm
    velocity: 0.03     // m/s/cm
  },
  age: {
    velocityPerYear: 0.4,      // 0.4% reduction per year after 60
    amplitudePerYear: 1.0,     // 1% reduction per year after 60
    durationPerYear: 0.05      // 0.05 ms increase per year after 60
  }
};

// 🔥 NUEVAS FUNCIONES PARA CORRECCIÓN FISIOLÓGICA CORRECTA
export interface TemperatureCorrectionResult {
  correctedValue: number;
  originalValue: number;
  correctionApplied: number;
  temperatureDifference: number;
  correctionType: 'duration' | 'amplitude' | 'velocity';
}

/**
 * Normaliza mediciones NCS a 32 °C: el frío alarga duración e hincha amplitud CMAP/SNAP y reduce velocidad aparente.
 */
export function applyTemperatureCorrection(
  value: number,
  currentTemperature: number,
  parameter: 'duration' | 'amplitude' | 'velocity'
): TemperatureCorrectionResult {
  const referenceTemperature = 32; // °C
  const temperatureDifference = referenceTemperature - currentTemperature;

  let correctedValue = value;
  let correctionApplied = 0;

  if (temperatureDifference > 0.5) {
    switch (parameter) {
      case 'duration':
        // Duración está artificialmente prolongada por el frío → reducirla
        correctionApplied = -(temperatureDifference * CORRECTION_FACTORS.temperature.duration);
        correctedValue = value + correctionApplied;
        break;

      case 'amplitude':
        // Amplitud artificialmente aumentada por el frío → reducirla (~% por °C vía CORRECTION_FACTORS)
        {
          const factor = temperatureDifference * CORRECTION_FACTORS.temperature.amplitude;
          correctionApplied = -(value * (factor / 100));
          correctedValue = value + correctionApplied;
        }
        break;

      case 'velocity':
        // Velocidad artificialmente disminuida por el frío → aumentarla
        correctionApplied = temperatureDifference * CORRECTION_FACTORS.temperature.velocity;
        correctedValue = value + correctionApplied;
        break;
    }
  }

  return {
    correctedValue: Math.max(0, correctedValue),
    originalValue: value,
    correctionApplied,
    temperatureDifference,
    correctionType: parameter
  };
}

// Criterios específicos para cada patrón
export const PATTERN_CRITERIA: PatternCriteria = {
  neuropathic: [
    {
      id: 'neuropathic_duration',
      description: 'Increased duration of motor unit potentials (>15ms)',
      weight: 3.0,
      specificity: 0.85,
      evaluationFunction: 'evaluateNeuropathicDuration',
      severityLevels: {
        mild: 15,
        moderate: 17,
        severe: 20
      }
    },
    {
      id: 'neuropathic_amplitude',
      description: 'Increased amplitude of motor unit potentials (>5mV)',
      weight: 2.5,
      specificity: 0.80,
      evaluationFunction: 'evaluateNeuropathicAmplitude',
      severityLevels: {
        mild: 5,
        moderate: 6,
        severe: 7
      }
    },
    {
      id: 'reduced_recruitment',
      description: 'Reduced recruitment pattern',
      weight: 2.0,
      specificity: 0.75,
      evaluationFunction: 'evaluateReducedRecruitment'
    },
    {
      id: 'fibrillations',
      description: 'Presence of fibrillations',
      weight: 1.5,
      specificity: 0.70,
      evaluationFunction: 'evaluateFibrillations'
    },
    {
      id: 'positive_waves',
      description: 'Presence of positive sharp waves',
      weight: 1.5,
      specificity: 0.70,
      evaluationFunction: 'evaluatePositiveWaves'
    },
    {
      id: 'complex_mups',
      description: 'Long duration, high amplitude polyphasic potentials',
      weight: 3.5,
      specificity: 0.90,
      evaluationFunction: 'evaluateComplexMUPs'
    }
  ],

  axonal: [
    {
      id: 'reduced_cmap_amplitude',
      description: 'Reduced amplitude of compound muscle action potential',
      weight: 3.0,
      specificity: 0.85,
      evaluationFunction: 'evaluateReducedCMAPAmplitude'
    },
    {
      id: 'preserved_velocity',
      description: 'Normal or slightly reduced conduction velocity',
      weight: 2.0,
      specificity: 0.75,
      evaluationFunction: 'evaluatePreservedVelocity'
    },
    {
      id: 'active_denervation',
      description: 'Fibrillations and positive waves in affected muscles',
      weight: 2.5,
      specificity: 0.80,
      evaluationFunction: 'evaluateActiveDenervation'
    },
    {
      id: 'reduced_snap_amplitude',
      description: 'Reduced sensory nerve action potential amplitude',
      weight: 2.8,
      specificity: 0.82,
      evaluationFunction: 'evaluateReducedSNAPAmplitude'
    }
  ],

  demyelinating: [
    {
      id: 'reduced_velocity',
      description: 'Markedly reduced conduction velocity',
      weight: 3.0,
      specificity: 0.90,
      evaluationFunction: 'evaluateReducedVelocity'
    },
    {
      id: 'prolonged_latency',
      description: 'Prolonged distal latency',
      weight: 2.5,
      specificity: 0.85,
      evaluationFunction: 'evaluateProlongedLatency'
    },
    {
      id: 'conduction_block',
      description: 'Conduction block',
      weight: 4.0,
      specificity: 0.95,
      evaluationFunction: 'evaluateConductionBlock'
    },
    {
      id: 'temporal_dispersion',
      description: 'Temporal dispersion',
      weight: 2.8,
      specificity: 0.88,
      evaluationFunction: 'evaluateTemporalDispersion'
    },
    {
      id: 'prolonged_f_waves',
      description: 'Prolonged or absent F-wave responses',
      weight: 2.2,
      specificity: 0.75,
      evaluationFunction: 'evaluateFWaveAbnormalities'
    }
  ],

  myopathic: [
    {
      id: 'decreased_duration',
      description: 'Decreased duration of motor unit potentials',
      weight: 2.5,
      specificity: 0.80,
      evaluationFunction: 'evaluateDecreasedDuration',
      severityLevels: {
        mild: 8,
        moderate: 6,
        severe: 5
      }
    },
    {
      id: 'decreased_amplitude',
      description: 'Decreased amplitude of motor unit potentials',
      weight: 2.0,
      specificity: 0.75,
      evaluationFunction: 'evaluateDecreasedAmplitude',
      severityLevels: {
        mild: 2,
        moderate: 1.5,
        severe: 1
      }
    },
    {
      id: 'early_recruitment',
      description: 'Early recruitment pattern',
      weight: 2.0,
      specificity: 0.70,
      evaluationFunction: 'evaluateEarlyRecruitment'
    },
    {
      id: 'increased_polyphasia',
      description: 'Increased polyphasia',
      weight: 1.5,
      specificity: 0.90,
      evaluationFunction: 'evaluateIncreasedPolyphasia'
    },
    {
      id: 'myopathic_recruitment',
      description: 'Rapid recruitment with reduced effort',
      weight: 2.3,
      specificity: 0.85,
      evaluationFunction: 'evaluateMyopathicRecruitment'
    }
  ],

  // 🔥 NUEVO PATRÓN - PRIORIDAD 4: Enfermedad de Motoneurona Superior
  motor_neuron_disease: [
    {
      id: 'widespread_denervation',
      description: 'Gold Coast: valid lower motor neuron signs in at least two body regions',
      weight: 6.0,
      specificity: 0.96,
      evaluationFunction: 'summarizeGoldCoastLMNRegions',
      severityLevels: {
        mild: 2,    // 2 regiones afectadas
        moderate: 3, // 3 regiones afectadas
        severe: 4   // 4+ regiones afectadas (cervical, torácica, lumbosacra, bulbar)
      }
    },
    {
      id: 'active_chronic_denervation_coexistence',
      description: 'Awaji-valid LMN signs: fibrillations/positive sharp waves or fasciculations with chronic neurogenic MUAP changes',
      weight: 5.5,
      specificity: 0.96,
      evaluationFunction: 'evaluateLMNSigns',
      severityLevels: {
        mild: 'limited_regions',
        moderate: 'multiple_regions', 
        severe: 'all_regions'
      }
    },
    {
      id: 'preserved_sensory_function',
      description: 'Preserved sensory nerve function (motor-predominant pattern)',
      weight: 3.0,
      specificity: 0.88,
      evaluationFunction: 'evaluatePreservedSensoryFunction'
    },
    {
      id: 'fasciculations_presence',
      description: 'Fasciculations count for ALS only when accompanied by chronic neurogenic MUAP changes (Awaji)',
      weight: 0,
      specificity: 0.65,
      evaluationFunction: 'evaluateAwajiFasciculationsWithChronicMuap'
    },
    {
      id: 'progressive_pattern',
      description: 'Progressive pattern with increasing severity over time',
      weight: 2.0,
      specificity: 0.70,
      evaluationFunction: 'evaluateProgressivePattern'
    },
    {
      id: 'large_neurogenic_mups',
      description: 'Large neurogenic motor unit potentials (>30ms duration, >15mV amplitude)',
      weight: 3.2,
      specificity: 0.90,
      evaluationFunction: 'evaluateLargeNeurogenicMUPs',
      severityLevels: {
        mild: { duration: 20, amplitude: 8000 },
        moderate: { duration: 25, amplitude: 12000 },
        severe: { duration: 30, amplitude: 15000 }
      }
    },
    {
      id: 'bulbar_involvement',
      description: 'Evidence of bulbar muscle involvement',
      weight: 3.5,
      specificity: 0.93,
      evaluationFunction: 'evaluateBulbarInvolvement'
    },
    {
      id: 'respiratory_muscle_involvement',
      description: 'Evidence of respiratory muscle involvement',
      weight: 3.0,
      specificity: 0.85,
      evaluationFunction: 'evaluateRespiratoryInvolvement'
    },
    {
      id: 'absence_conduction_block',
      description: 'Absence of focal neuropathy or motor conduction block to exclude multifocal motor neuropathy',
      weight: 5.0,
      specificity: 0.90,
      evaluationFunction: 'evaluateAbsenceConductionBlock'
    },
    {
      id: 'motor_unit_instability',
      description: 'Motor unit instability and variability in morphology',
      weight: 2.8,
      specificity: 0.82,
      evaluationFunction: 'evaluateMotorUnitInstability'
    }
  ]
};

// Diagnósticos diferenciales enriquecidos
export const DIFFERENTIAL_DIAGNOSES = {
  neuropathic: {
    acute_denervation: [
      'Trauma agudo de nervio periférico',
      'Síndrome de Guillain-Barré (variante axonal)',
      'Radiculopatía aguda',
      'Mononeuropatía aguda',
      'Plexopatía traumática'
    ],
    chronic_reinnervation: [
      'Compresión nerviosa crónica',
      'Enfermedad de motoneurona',
      'Neuropatía hereditaria',
      'Radiculopatía crónica',
      'Secuelas de poliomielitis'
    ],
    general: [
      'Neuropatía periférica',
      'Radiculopatía',
      'Plexopatía',
      'Mononeuropatía múltiple'
    ]
  },
  axonal: [
    'Neuropatía diabética',
    'Neuropatía alcohólica',
    'Neuropatía tóxica',
    'Neuropatía nutricional',
    'Síndrome de Guillain-Barré (variante axonal)',
    'Neuropatía crítica del paciente',
    'Vasculitis con afectación nerviosa'
  ],
  demyelinating: [
    'Síndrome de Guillain-Barré (variante desmielinizante)',
    'Polineuropatía desmielinizante inflamatoria crónica (PDIC)',
    'Neuropatía hereditaria con predisposición a parálisis por presión',
    'Síndrome de túnel carpiano severo',
    'Neuropatía desmielinizante hereditaria (CMT tipo 1)',
    'Gammapatía monoclonal con neuropatía',
    'Neuropatía multifocal motora'
  ],
  myopathic: [
    'Miopatía inflamatoria (polimiositis, dermatomiositis)',
    'Distrofia muscular, especialmente si hay actividad espontánea activa o antecedente familiar',
    'Miopatía metabólica',
    'Miopatía mitocondrial',
    'Miopatía inducida por fármacos/tóxicos (p. ej., estatinas, colchicina)',
    'Miopatía necrotizante autoinmune; las fibrilaciones obligan a descartarla junto con miopatías tóxicas y distrofias musculares activas',
    'Miopatía endocrina (hipertiroidismo, hipotiroidismo)',
    'Miopatía por depósito (amiloidosis)',
    'Miastenia gravis (en casos selectos)'
  ],
  
  // 🔥 NUEVO - PRIORIDAD 4: Diagnósticos diferenciales para enfermedad de motoneurona
  motor_neuron_disease: [
    'Esclerosis Lateral Amiotrófica (ELA) - forma clásica',
    'ELA de inicio bulbar',
    'ELA de inicio espinal',
    'Atrofia Muscular Espinal (AME) del adulto',
    'Enfermedad de Kennedy (AMEX)',
    'Atrofia muscular progresiva',
    'Esclerosis lateral primaria',
    'Parálisis bulbar progresiva',
    'Enfermedad de motoneurona multifocal',
    'Síndrome post-polio',
    'Mielopatía cervical espondilótica con radiculopatía múltiple',
    'Polirradiculopatía desmielinizante inflamatoria crónica (PDIC) - variante motora',
    'Neuropatía motora multifocal con bloqueos de conducción'
  ]
};

// Recomendaciones específicas y accionables
export const CLINICAL_RECOMMENDATIONS = {
  neuropathic: {
    acute_denervation: [
      'Considerar estudio de seguimiento en 3-4 semanas para evaluar signos de reinervación',
      'Evaluación neurológica urgente si sospecha de síndrome de Guillain-Barré',
      'Investigar historia de trauma reciente o procedimientos invasivos',
      'Considerar resonancia magnética si se sospecha radiculopatía',
      'Evaluar función respiratoria si afectación es generalizada'
    ],
    chronic_reinnervation: [
      'Considerar estudio de neuroconducción comparativo con extremidad contralateral',
      'Evaluación de enfermedad de motoneurona si patrón es generalizado',
      'Investigar compresión crónica y considerar liberación quirúrgica',
      'Seguimiento electromiográfico en 6-12 meses',
      'Evaluación genética si historia familiar positiva'
    ],
    general: [
      'Correlacionar con estudios de conducción nerviosa',
      'Evaluación clínica neurológica completa',
      'Considerar estudios de imagen según distribución'
    ]
  },
  axonal: [
    'Investigar causas metabólicas: diabetes, deficiencias vitamínicas',
    'Historia detallada de exposición a tóxicos y medicamentos',
    'Evaluación de función renal y hepática',
    'Considerar biopsia de nervio si etiología unclear',
    'Seguimiento de progresión con estudios seriados',
    'Optimización del control glucémico si diabético'
  ],
  demyelinating: [
    'Considerar punción lumbar para análisis de LCR',
    'Evaluación inmunológica completa',
    'Descartar gammapatía monoclonal',
    'Considerar tratamiento inmunosupresor si PDIC',
    'Vigilar función respiratoria si síndrome de Guillain-Barré',
    'Evaluación genética si sospecha de neuropatía hereditaria'
  ],
  myopathic: [
    'Medición de enzimas musculares (CK, LDH, aldolasa)',
    'Evaluación de función tiroidea',
    'Considerar biopsia muscular si diagnóstico unclear',
    'Revisión de medicamentos potencialmente miotóxicos',
    'Evaluación cardiológica si sospecha de miopatía sistémica',
    'Estudios metabólicos si sospecha de miopatía metabólica'
  ],
  
  // 🔥 NUEVO - PRIORIDAD 4: Recomendaciones para enfermedad de motoneurona
  motor_neuron_disease: [
    '🚨 URGENTE: Referencia inmediata a neurólogo especialista en enfermedades de motoneurona',
    'Evaluación respiratoria completa (espirometría, gasometría arterial)',
    'Evaluación de función bulbar (disfagia, disartria)',
    'Resonancia magnética cerebral y medular para descartar causas estructurales',
    'Punción lumbar si sospecha de proceso inflamatorio',
    'Estudios genéticos si historia familiar positiva o sospecha de AME/Kennedy',
    'Evaluación multidisciplinaria: neumología, nutrición, fisioterapia',
    'Planificación temprana de cuidados paliativos y directivas avanzadas',
    'Seguimiento electromiográfico cada 3-6 meses para documentar progresión',
    'Evaluación de necesidad de ventilación asistida',
    'Considerar participación en ensayos clínicos',
    'Descartar miméticos tratables: PDIC motora, neuropatía multifocal motora',
    'Evaluación de función cognitiva (ELA-DFT)',
    'Soporte psicosocial para paciente y familia'
  ]
};

// Rangos de referencia estratificados por edad para PUMs
export const AGE_STRATIFIED_REFERENCES = {
  motorUnitPotentials: {
    duration: {
      '18-39': { mean: 10.5, std: 2.3, upperLimit: 15 },
      '40-59': { mean: 11.8, std: 2.8, upperLimit: 17 },
      '60-79': { mean: 13.2, std: 3.2, upperLimit: 19 },
      '80+': { mean: 14.5, std: 3.5, upperLimit: 21 }
    },
    amplitude: {
      '18-39': { mean: 8.5, std: 4.2, lowerLimit: 2.0 },
      '40-59': { mean: 7.8, std: 4.0, lowerLimit: 1.8 },
      '60-79': { mean: 6.9, std: 3.5, lowerLimit: 1.5 },
      '80+': { mean: 5.8, std: 3.0, lowerLimit: 1.2 }
    }
  }
};

// 🔥 NUEVOS RANGOS FISIOLÓGICOS PARA VALIDACIÓN
export interface PhysiologicalRanges {
  ncs: {
    motor: {
      latency: { min: number; max: number; unit: string };
      amplitude: { min: number; max: number; unit: string };
      velocity: { min: number; max: number; unit: string };
    };
    sensory: {
      latency: { min: number; max: number; unit: string };
      amplitude: { min: number; max: number; unit: string };
      velocity: { min: number; max: number; unit: string };
    };
    lateResponses: {
      fWaveLatencyUpperLimb: { min: number; max: number; unit: string };
      fWaveLatencyLowerLimb: { min: number; max: number; unit: string };
      hReflexSideToSideDiff: { min: number; max: number; unit: string };
    };
  };
  emg: {
    motorUnitPotentials: {
      duration: { min: number; max: number; unit: string };
      amplitude: { min: number; max: number; unit: string };
      polyphasia: { min: number; max: number; unit: string };
    };
    temperature: { min: number; max: number; unit: string };
  };
  general: {
    age: { min: number; max: number; unit: string };
    height: { min: number; max: number; unit: string };
    weight: { min: number; max: number; unit: string };
  };
}

// Rangos fisiológicos basados en literatura médica y práctica clínica
export const PHYSIOLOGICAL_RANGES: PhysiologicalRanges = {
  ncs: {
    motor: {
      // Latencias distales motoras típicas
      latency: { min: 1.0, max: 15.0, unit: 'ms' },
      // Amplitudes CMAP (Compound Muscle Action Potential)
      amplitude: { min: 0.1, max: 50.0, unit: 'mV' },
      // Velocidades de conducción motora
      velocity: { min: 25.0, max: 80.0, unit: 'm/s' }
    },
    sensory: {
      // Latencias distales sensitivas típicas
      latency: { min: 1.0, max: 10.0, unit: 'ms' },
      // Amplitudes SNAP (Sensory Nerve Action Potential)
      amplitude: { min: 1.0, max: 200.0, unit: 'μV' },
      // Velocidades de conducción sensitiva
      velocity: { min: 25.0, max: 80.0, unit: 'm/s' }
    },
    lateResponses: {
      fWaveLatencyUpperLimb: { min: 20.0, max: 32.0, unit: 'ms' },
      fWaveLatencyLowerLimb: { min: 40.0, max: 56.0, unit: 'ms' },
      hReflexSideToSideDiff: { min: 0.0, max: 1.5, unit: 'ms' }
    }
  },
  emg: {
    motorUnitPotentials: {
      // Duración de potenciales de unidad motora
      duration: { min: 2.0, max: 50.0, unit: 'ms' },
      // Amplitud de potenciales de unidad motora
      amplitude: { min: 50.0, max: 15000.0, unit: 'μV' },
      // Porcentaje de polifasia
      polyphasia: { min: 0.0, max: 100.0, unit: '%' }
    },
    // Temperatura corporal durante el estudio
    temperature: { min: 30.0, max: 40.0, unit: '°C' }
  },
  general: {
    // Edad del paciente
    age: { min: 0, max: 120, unit: 'años' },
    // Altura del paciente
    height: { min: 30, max: 250, unit: 'cm' },
    // Peso del paciente
    weight: { min: 1, max: 300, unit: 'kg' }
  }
};

// Validación de severidad basada en desviaciones de rangos normales
export interface SeverityThresholds {
  mild: { factor: number; description: string };
  moderate: { factor: number; description: string };
  severe: { factor: number; description: string };
  critical: { factor: number; description: string };
}

export const SEVERITY_THRESHOLDS: SeverityThresholds = {
  mild: { factor: 1.5, description: '1.5x fuera del rango normal' },
  moderate: { factor: 2.0, description: '2x fuera del rango normal' },
  severe: { factor: 3.0, description: '3x fuera del rango normal' },
  critical: { factor: 5.0, description: '5x fuera del rango normal o más' }
};

// Tipos de errores de validación fisiológica
export type ValidationSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface ValidationResult {
  isValid: boolean;
  severity: ValidationSeverity;
  message: string;
  expectedRange: string;
  actualValue: number;
  field: string;
  suggestions?: string[];
}

// Funciones auxiliares para validación
export function getAgeCategory(age: number): keyof typeof AGE_STRATIFIED_REFERENCES.motorUnitPotentials.duration {
  if (age < 40) return '18-39';
  if (age < 60) return '40-59';
  if (age < 80) return '60-79';
  return '80+';
}

export function calculateSeverityLevel(value: number, range: { min: number; max: number }): keyof SeverityThresholds | null {
  const isOutsideRange = value < range.min || value > range.max;
  if (!isOutsideRange) return null;

  const deviation = Math.max(
    range.min > 0 ? Math.abs(value - range.min) / range.min : Math.abs(value - range.min),
    range.max > 0 ? Math.abs(value - range.max) / range.max : Math.abs(value - range.max)
  );

  if (deviation >= SEVERITY_THRESHOLDS.critical.factor) return 'critical';
  if (deviation >= SEVERITY_THRESHOLDS.severe.factor) return 'severe';
  if (deviation >= SEVERITY_THRESHOLDS.moderate.factor) return 'moderate';
  return 'mild';
}

// ========== NUEVOS CRITERIOS: ENFERMEDAD DE NEURONA MOTORA SUPERIOR ==========
export const upper_motor_neuron_disease = {
  name: 'Enfermedad de Neurona Motora Superior',
  description: 'Patrones característicos de afectación de la neurona motora superior',
  criteria: [
    {
      id: 'spasticity',
      description: 'Espasticidad significativa (escala Ashworth modificada > 2)',
      weight: 0.25,
      specificity: 0.85,
      evaluationFunction: 'evaluateSpasticity'
    },
    {
      id: 'hyperreflexia',
      description: 'Hiperreflexia presente',
      weight: 0.20,
      specificity: 0.80,
      evaluationFunction: 'evaluateHyperreflexia'
    },
    {
      id: 'babinski_sign',
      description: 'Signo de Babinski positivo',
      weight: 0.15,
      specificity: 0.90,
      evaluationFunction: 'evaluateBabinskiSign'
    },
    {
      id: 'clonus',
      description: 'Clonus presente',
      weight: 0.15,
      specificity: 0.85,
      evaluationFunction: 'evaluateClonus'
    },
    {
      id: 'weakness_pattern',
      description: 'Patrón de debilidad piramidal',
      weight: 0.15,
      specificity: 0.75,
      evaluationFunction: 'evaluateWeaknessPattern'
    },
    {
      id: 'muscle_tone',
      description: 'Tono muscular aumentado',
      weight: 0.10,
      specificity: 0.70,
      evaluationFunction: 'evaluateMuscleTone'
    }
  ],
  severity: {
    mild: { minScore: 0.4, maxScore: 0.6 },
    moderate: { minScore: 0.6, maxScore: 0.8 },
    severe: { minScore: 0.8, maxScore: 1.0 }
  },
  clinicalCorrelation: {
    symptoms: [
      'Debilidad muscular progresiva',
      'Espasticidad',
      'Hiperreflexia',
      'Signo de Babinski positivo',
      'Clonus',
      'Dificultad para caminar'
    ],
    distribution: [
      'Afectación bilateral',
      'Predominio en músculos extensores de brazos',
      'Predominio en músculos flexores de piernas',
      'Patrón descendente'
    ],
    emgFindings: [
      'Actividad EMG normal en reposo',
      'Patrón de reclutamiento reducido',
      'Unidades motoras normales o ligeramente aumentadas',
      'Velocidades de conducción normales'
    ]
  }
};

// ========== NUEVOS CRITERIOS: ENFERMEDAD DE NEURONA MOTORA INFERIOR ==========
export const lower_motor_neuron_disease = {
  name: 'Enfermedad de Neurona Motora Inferior',
  description: 'Patrones característicos de afectación de la neurona motora inferior',
  criteria: [
    {
      id: 'awaji_fasciculations_with_chronic_muap',
      description: 'Fasciculaciones presentes sólo son signo NMI válido si coexisten con PAUMs neurogénicos crónicos',
      weight: 0.25,
      specificity: 0.92,
      evaluationFunction: 'evaluateLMNSigns'
    },
    {
      id: 'muscle_atrophy',
      description: 'Atrofia muscular significativa',
      weight: 0.20,
      specificity: 0.80,
      evaluationFunction: 'evaluateMuscleAtrophy'
    },
    {
      id: 'hyporeflexia',
      description: 'Hiporreflexia o arreflexia',
      weight: 0.15,
      specificity: 0.75,
      evaluationFunction: 'evaluateHyporeflexia'
    },
    {
      id: 'fibrillations',
      description: 'Fibrilaciones presentes en EMG',
      weight: 0.20,
      specificity: 0.90,
      evaluationFunction: 'evaluateLMNSigns'
    },
    {
      id: 'positive_sharp_waves',
      description: 'Ondas agudas positivas en EMG',
      weight: 0.20,
      specificity: 0.85,
      evaluationFunction: 'evaluateLMNSigns'
    },
    {
      id: 'enlarged_motor_units',
      description: 'PAUMs neurogénicos crónicos: amplitud aumentada, duración prolongada o polifasia aumentada',
      weight: 0.20,
      specificity: 0.80,
      evaluationFunction: 'evaluateLMNSigns'
    }
  ],
  severity: {
    mild: { minScore: 0.4, maxScore: 0.6 },
    moderate: { minScore: 0.6, maxScore: 0.8 },
    severe: { minScore: 0.8, maxScore: 1.0 }
  },
  clinicalCorrelation: {
    symptoms: [
      'Debilidad muscular progresiva',
      'Atrofia muscular',
      'Fasciculaciones',
      'Calambres musculares',
      'Hiporreflexia o arreflexia'
    ],
    distribution: [
      'Afectación focal o multifocal',
      'Progresión asimétrica',
      'Puede ser segmentaria'
    ],
    emgFindings: [
      'Actividad espontánea anormal (fibrilaciones, ondas agudas)',
      'Fasciculaciones',
      'Unidades motoras aumentadas',
      'Patrón de reclutamiento reducido',
      'Velocidades de conducción pueden estar reducidas'
    ]
  }
};

// ========== NUEVOS CRITERIOS: ESCLEROSIS LATERAL AMIOTRÓFICA (ELA) ==========
export const amyotrophic_lateral_sclerosis = {
  name: 'Esclerosis Lateral Amiotrófica (ELA)',
  description: 'Criterios diagnósticos para ELA (combinación de neurona motora superior e inferior)',
  criteria: [
    {
      id: 'upper_motor_neuron_signs',
      description: 'Signos de neurona motora superior en múltiples regiones',
      weight: 0.25,
      specificity: 0.90,
      evaluationFunction: 'evaluateUpperMotorNeuronSigns'
    },
    {
      id: 'lower_motor_neuron_signs',
      description: 'Signos NMI válidos por Awaji en al menos 2 regiones Gold Coast',
      weight: 0.45,
      specificity: 0.96,
      evaluationFunction: 'summarizeGoldCoastLMNRegions'
    },
    {
      id: 'progressive_weakness',
      description: 'Debilidad progresiva documentada',
      weight: 0.15,
      specificity: 0.80,
      evaluationFunction: 'evaluateProgressiveWeakness'
    },
    {
      id: 'bulbar_involvement',
      description: 'Afectación bulbar (disartria, disfagia)',
      weight: 0.10,
      specificity: 0.85,
      evaluationFunction: 'evaluateBulbarInvolvement'
    },
    {
      id: 'respiratory_involvement',
      description: 'Afectación respiratoria',
      weight: 0.10,
      specificity: 0.90,
      evaluationFunction: 'evaluateRespiratoryInvolvement'
    }
  ],
  severity: {
    mild: { minScore: 0.5, maxScore: 0.7 },
    moderate: { minScore: 0.7, maxScore: 0.85 },
    severe: { minScore: 0.85, maxScore: 1.0 }
  },
  clinicalCorrelation: {
    symptoms: [
      'Debilidad progresiva de múltiples grupos musculares',
      'Atrofia muscular',
      'Fasciculaciones generalizadas',
      'Espasticidad',
      'Disartria y disfagia (formas bulbares)',
      'Dificultad respiratoria (etapas avanzadas)'
    ],
    distribution: [
      'Afectación de múltiples regiones corporales',
      'Progresión desde focal a generalizada',
      'Puede iniciar en extremidades o región bulbar'
    ],
    emgFindings: [
      'Denervación aguda y crónica',
      'Fasciculaciones generalizadas',
      'Reinervación (unidades motoras gigantes)',
      'Afectación de múltiples territorios nerviosos',
      'Conducción nerviosa relativamente preservada'
    ]
  }
};

/**
 * Anastomosis de Martin-Gruber: amplitud proximal en antebrazo mayor que en muñeca (mediano motor).
 * Asume resultados ordenados de distal a proximal.
 */
export function detectMartinGruber(medianMotorResults: NCSTestResult[]): boolean {
  if (medianMotorResults.length < 2) return false;

  const n0 = medianMotorResults[0].nerve.toLowerCase();
  const isMedian = n0.includes('mediano') || n0.includes('median');
  if (!isMedian) return false;

  const distal = medianMotorResults[0];
  const proximal = medianMotorResults[1];

  if (
    proximal.amplitude !== undefined &&
    distal.amplitude !== undefined &&
    proximal.amplitude > distal.amplitude * 1.1
  ) {
    return true;
  }
  return false;
}

/**
 * Patrón "sural sparing" (p. ej. AIDP): SNAPs de miembro superior muy bajos pero sural conservado.
 */
export function detectSuralSparing(
  upperLimbSnaps: NCSTestResult[],
  suralSnap: NCSTestResult
): boolean {
  if (upperLimbSnaps.length === 0) return false;

  const upperLimbAbnormal = upperLimbSnaps.every(
    r => r.amplitude !== undefined && r.amplitude < 5
  );
  const suralNormal = suralSnap.amplitude !== undefined && suralSnap.amplitude > 6;

  return upperLimbAbnormal && suralNormal;
}

/**
 * Posible estimulación con polaridad invertida: latencias muy prolongadas pero velocidades normales o rápidas en todos los registros.
 */
export function detectPolarityInversionError(ncsResults: NCSTestResult[]): boolean {
  if (ncsResults.length === 0) return false;

  let prolongedLatencyCount = 0;
  let normalVelocityCount = 0;

  for (const r of ncsResults) {
    if (r.latency !== undefined && r.latency !== null && r.latency > 5.5) prolongedLatencyCount++;
    if (r.velocity !== undefined && r.velocity !== null && r.velocity >= 50) normalVelocityCount++;
  }

  return prolongedLatencyCount > 1 && normalVelocityCount === ncsResults.length;
}

/** Alertas derivadas de criterios modernos NCS (duración CMAP, MG, sural sparing, polaridad). */
export interface NcsIntegratedAlert {
  id: string;
  urgency: 'low' | 'medium' | 'high' | 'urgent';
  message: string;
}

/** Umbral de duración distal CMAP (ms) según nervio; null si no aplica la tabla publicada. */
export function resolveCmapMaxDurationThresholdMs(nerveName: string): number | null {
  const n = nerveName.toLowerCase();
  if (n.includes('mediano') || n.includes('median')) return CMAP_MAX_DURATION_THRESHOLDS.mediano;
  if (n.includes('cubital') || n.includes('ulnar')) return CMAP_MAX_DURATION_THRESHOLDS.cubital;
  if (n.includes('perone') || n.includes('fibular')) return CMAP_MAX_DURATION_THRESHOLDS.peroneo;
  if (n.includes('tibial')) return CMAP_MAX_DURATION_THRESHOLDS.tibial;
  return null;
}

export function collectNcsIntegratedAlerts(ncsResults: NCSTestResult[]): NcsIntegratedAlert[] {
  const alerts: NcsIntegratedAlert[] = [];
  if (!ncsResults?.length) return alerts;

  for (const r of ncsResults) {
    if (r.type !== 'motor' || r.duration === undefined || r.duration === null || !Number.isFinite(r.duration)) {
      continue;
    }
    const maxMs = resolveCmapMaxDurationThresholdMs(r.nerve);
    if (maxMs === null) continue;
    if (r.duration > maxMs) {
      const stdTemp = ncsStandardTemperatureC(r.nerve);
      const isCold =
        r.temperature !== undefined &&
        r.temperature !== null &&
        Number.isFinite(r.temperature) &&
        r.temperature < stdTemp;
      const tempNote = isCold
        ? ` Temperatura cutánea registrada (${r.temperature}°C) por debajo del estándar (${stdTemp}°C); la duración puede estar artificialmente prolongada por frío.`
        : '';
      alerts.push({
        id: `cmap_duration_${r.id}`,
        urgency: isCold ? 'low' : 'medium',
        message: `Duración distal del CMAP aumentada en ${r.nerve} (${r.side}): ${r.duration.toFixed(1)} ms (referencia típica ≤ ${maxMs} ms).${tempNote} Valorar dispersión temporal / desmielinización según contexto.`,
      });
    }
  }

  for (const side of ['left', 'right'] as const) {
    const medianMotors = ncsResults.filter(
      x =>
        x.type === 'motor' &&
        x.side === side &&
        (x.nerve.toLowerCase().includes('median') || x.nerve.toLowerCase().includes('mediano'))
    );
    if (medianMotors.length < 2) continue;
    const sorted = [...medianMotors].sort((a, b) => a.latency - b.latency);
    const pair = sorted.slice(0, 2);
    if (detectMartinGruber(pair)) {
      alerts.push({
        id: `martin_gruber_median_${side}`,
        urgency: 'medium',
        message: `Patrón compatible con anastomosis Martin-Gruber (mediano motor, ${side}): amplitud proximal > distal (>10%). Interpretar con precaución bloqueos parciales en antebrazo.`,
      });
    }
  }

  const sural = ncsResults.find(
    x => x.type === 'sensory' && x.nerve?.toLowerCase().includes('sural')
  );
  const upperSnaps = ncsResults.filter(x => {
    if (x.type !== 'sensory') return false;
    const nn = x.nerve?.toLowerCase() || '';
    if (/(sural|tibial|perone|fibular|saphenous|safeno)/i.test(nn)) return false;
    return /mediano|median|ulnar|cubital|radial|musculocut/i.test(nn);
  });
  if (sural && upperSnaps.length > 0 && detectSuralSparing(upperSnaps, sural)) {
    alerts.push({
      id: 'sural_sparing_pattern',
      urgency: 'high',
      message:
        'Patrón de sural sparing (SNAPs de miembro superior muy reducidos con sural relativamente conservado): puede ser muy sugestivo de polirradiculoneuropatía desmielinizante aguda (p. ej. GBS/AIDP). Correlación clínica urgente.',
    });
  }

  if (detectPolarityInversionError(ncsResults)) {
    alerts.push({
      id: 'polarity_inversion_suspect',
      urgency: 'medium',
      message:
        'Posible error técnico: varias latencias muy prolongadas con velocidades normal-altas en todos los registros. Verifique colocación de cátodo/ánodo y técnica de estimulación antes de interpretar las latencias.',
    });
  }

  return alerts;
} 